import axios from "axios";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "Actividades";

/**
 * Ruta del backend para generar el documento Word.
 */
const obtenerUrlGenerarAsistencia = (actividadId) =>
  `/api/asistencias/actividades/${actividadId}/generar-registro-asistencia`;

/**
 * Configuración de autenticación para Axios.
 */
const obtenerAuthConfig = () => {
  const token = localStorage.getItem("token");

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

/**
 * Normaliza textos para comparar los nombres de los módulos.
 */
const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

/**
 * Convierte cualquier fecha ISO al formato dd/mm/aaaa.
 *
 * Ejemplos:
 * 2026-08-04T00:00:00.000Z -> 04/08/2026
 * 2026-08-04                -> 04/08/2026
 * 04/08/2026                -> 04/08/2026
 *
 * No utiliza new Date(), por lo tanto la zona horaria
 * nunca puede cambiar el día registrado.
 */
const formatearFecha = (fecha) => {
  if (!fecha) {
    return "—";
  }

  const valorFecha =
    typeof fecha === "object" && fecha?.$date
      ? fecha.$date
      : fecha;

  const valor = String(valorFecha).trim();

  // Si ya está en formato dd/mm/aaaa, se conserva.
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(valor)) {
    return valor;
  }

  // Extrae directamente aaaa-mm-dd de una fecha ISO.
  const coincidencia = valor.match(
    /^(\d{4})-(\d{2})-(\d{2})/
  );

  if (!coincidencia) {
    return "—";
  }

  const [, anio, mes, dia] = coincidencia;

  return `${dia}/${mes}/${anio}`;
};

/**
 * Limpia el nombre de una actividad para usarlo
 * como parte del nombre del archivo descargado.
 */
const limpiarNombreArchivo = (valor = "") =>
  String(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80) || "Actividad";

/**
 * Obtiene el nombre del archivo enviado por el backend.
 */
const obtenerNombreArchivo = (
  contentDisposition,
  nombreActividad
) => {
  const nombrePredeterminado =
    `Registro_Asistencia_${limpiarNombreArchivo(
      nombreActividad
    )}.docx`;

  if (!contentDisposition) {
    return nombrePredeterminado;
  }

  const utf8Match = contentDisposition.match(
    /filename\*=UTF-8''([^;]+)/i
  );

  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(
        utf8Match[1]
          .replace(/["']/g, "")
          .trim()
      );
    } catch (error) {
      console.warn(
        "No se pudo decodificar el nombre del archivo:",
        error
      );
    }
  }

  const normalMatch = contentDisposition.match(
    /filename="?([^";]+)"?/i
  );

  return (
    normalMatch?.[1]?.trim() ||
    nombrePredeterminado
  );
};

/**
 * Lee mensajes de error cuando Axios recibe un Blob.
 */
const obtenerMensajeErrorBlob = async (error) => {
  const data = error?.response?.data;

  if (data instanceof Blob) {
    try {
      const contenido = await data.text();
      const json = JSON.parse(contenido);

      return (
        json?.message ||
        json?.error ||
        "No se pudo generar el registro de asistencia."
      );
    } catch (blobError) {
      console.error(
        "No se pudo leer el error del servidor:",
        blobError
      );
    }
  }

  return (
    data?.message ||
    data?.error ||
    error?.message ||
    "No se pudo generar el registro de asistencia."
  );
};

const ListarActividad = ({ refresh }) => {
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);

  const navigate = useNavigate();
  const { id } = useParams();

  /**
   * Obtiene la configuración de permisos del módulo.
   */
  const moduloActividades = useMemo(() => {
    try {
      const permisosStorage =
        localStorage.getItem("permisos");

      const usuarioStorage =
        localStorage.getItem("usuario");

      const userStorage =
        localStorage.getItem("user");

      const usuariosStorage =
        localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo ||
            modulo?.modulo ||
            modulo?.nombre ||
            "";

          return (
            normalizarTexto(nombre) ===
            moduloBuscado
          );
        }) || {}
      );
    } catch (error) {
      console.error(
        "Error leyendo permisos:",
        error
      );

      return {};
    }
  }, []);

  const puedeCrear =
    moduloActividades?.crear === true ||
    moduloActividades?.puedeCrear === true ||
    moduloActividades?.create === true;

  const puedeActualizar =
    moduloActividades?.actualizar === true ||
    moduloActividades?.editar === true ||
    moduloActividades?.puedeActualizar === true ||
    moduloActividades?.update === true;

  const puedeEliminar =
    moduloActividades?.eliminar === true ||
    moduloActividades?.puedeEliminar === true ||
    moduloActividades?.delete === true;

  /**
   * Gestiona una sesión vencida.
   */
  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status !== 401) {
        return false;
      }

      Swal.fire({
        icon: "warning",
        title: "Sesión expirada",
        text: "Por favor inicia sesión nuevamente.",
        confirmButtonText: "Aceptar",
      }).then(() => {
        localStorage.removeItem("token");
        navigate("/login");
      });

      return true;
    },
    [navigate]
  );

  /**
   * Genera y descarga el documento Word.
   */
  const generarAsistencia = useCallback(
    async (actividad) => {
      const actividadId = actividad?._id;

      if (!actividadId) {
        Swal.fire(
          "Error",
          "No se encontró el ID de la actividad.",
          "error"
        );

        return;
      }

      try {
        setGeneratingId(actividadId);

        const response = await axios.get(
          obtenerUrlGenerarAsistencia(actividadId),
          {
            ...obtenerAuthConfig(),
            responseType: "blob",
          }
        );

        const contentType =
          response.headers?.["content-type"] || "";

        if (
          contentType.includes("application/json")
        ) {
          const contenido =
            await response.data.text();

          const json = JSON.parse(contenido);

          throw new Error(
            json?.message ||
              json?.error ||
              "El servidor no devolvió un documento Word."
          );
        }

        const archivo = new Blob(
          [response.data],
          {
            type:
              contentType ||
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          }
        );

        const urlTemporal =
          window.URL.createObjectURL(archivo);

        const enlace =
          document.createElement("a");

        const contentDisposition =
          response.headers?.[
            "content-disposition"
          ] || "";

        enlace.href = urlTemporal;
        enlace.download = obtenerNombreArchivo(
          contentDisposition,
          actividad.nombreActividad
        );

        document.body.appendChild(enlace);
        enlace.click();
        enlace.remove();

        window.URL.revokeObjectURL(urlTemporal);

        await Swal.fire({
          icon: "success",
          title: "Documento generado",
          text: "El registro de asistencia se descargó correctamente.",
          showConfirmButton: false,
          timer: 1800,
        });
      } catch (error) {
        console.error(
          "Error al generar el registro de asistencia:",
          error
        );

        if (manejarErrorSesion(error)) {
          return;
        }

        const mensaje =
          await obtenerMensajeErrorBlob(error);

        Swal.fire("Error", mensaje, "error");
      } finally {
        setGeneratingId(null);
      }
    },
    [manejarErrorSesion]
  );

  /**
   * Elimina una actividad.
   */
  const deleteActividad = useCallback(
    async (actividadId) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Acceso denegado",
          "No tienes permisos para eliminar actividades.",
          "warning"
        );

        return;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡No podrá revertir este proceso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) {
          return;
        }

        setDeletingId(actividadId);

        const { data } = await axios.delete(
          `/api/actividad/delete/${actividadId}`,
          obtenerAuthConfig()
        );

        setActividades((prev) =>
          prev.filter(
            (actividad) =>
              actividad._id !== actividadId
          )
        );

        await Swal.fire({
          icon: "success",
          title:
            data?.message ||
            "Actividad eliminada",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error(
          "Error al eliminar la actividad:",
          error
        );

        if (manejarErrorSesion(error)) {
          return;
        }

        Swal.fire(
          "Error",
          "No se pudo eliminar la actividad.",
          "error"
        );
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  /**
   * Obtiene las actividades y transforma la fecha antes
   * de guardar los registros en el estado.
   *
   * El DataTable recibe directamente:
   * 04/08/2026
   *
   * Nunca recibe:
   * 2026-08-04T00:00:00.000Z
   */
  const getData = useCallback(async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.get(
        `/api/actividad/listbyid/${id}`,
        obtenerAuthConfig()
      );

      const lista = Array.isArray(data)
        ? data
        : data?.actividades || [];

      const listaFormateada = [...lista]
        .map((actividad) => ({
          ...actividad,

          // Se conserva solamente como respaldo.
          fechaOriginal: actividad.fecha,

          // Este es el valor que recibe DataTable.
          fecha: formatearFecha(actividad.fecha),
        }))
        .reverse();

      setActividades(listaFormateada);
    } catch (error) {
      console.error(
        "Error al cargar las actividades:",
        error
      );

      if (manejarErrorSesion(error)) {
        return;
      }

      Swal.fire(
        "Error",
        "No se pudo cargar la lista de actividades.",
        "error"
      );

      setActividades([]);
    } finally {
      setLoading(false);
    }
  }, [id, manejarErrorSesion]);

  useEffect(() => {
    getData();
  }, [getData, refresh]);

  /**
   * Agrega las acciones a cada registro.
   */
  const actividadesConFunciones = useMemo(
    () =>
      actividades.map((actividad) => {
        const procesandoActividad =
          deletingId === actividad._id ||
          generatingId === actividad._id;

        return {
          ...actividad,
          funciones: (
            <div className="d-flex gap-2 flex-nowrap justify-content-end align-items-center">
              {/* TOMAR ASISTENCIA */}
              <button
                className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(
                    `/asistencia/${actividad._id}`
                  )
                }
                title="Tomar asistencia"
                type="button"
                disabled={
                  loading || procesandoActividad
                }
              >
                <i className="fa-solid fa-list-check"></i>
              </button>

              {/* GENERAR DOCUMENTO WORD */}
              <button
                className="btn btn-success btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  generarAsistencia(actividad)
                }
                title="Generar registro de asistencia en Word"
                type="button"
                disabled={
                  loading || procesandoActividad
                }
              >
                {generatingId === actividad._id ? (
                  <i className="fa-solid fa-spinner fa-spin text-white"></i>
                ) : (
                  <i className="fa-solid fa-file-word text-white"></i>
                )}
              </button>

              {/* EDITAR ACTIVIDAD */}
              {puedeActualizar && (
                <button
                  className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  onClick={() =>
                    navigate(
                      `/actividad/editar/${actividad._id}`
                    )
                  }
                  title="Editar actividad"
                  type="button"
                  disabled={
                    loading || procesandoActividad
                  }
                >
                  <i className="fa-solid fa-pen-to-square"></i>
                </button>
              )}

              {/* ELIMINAR ACTIVIDAD */}
              {puedeEliminar && (
                <button
                  className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  onClick={() =>
                    deleteActividad(actividad._id)
                  }
                  title="Eliminar actividad"
                  type="button"
                  disabled={
                    loading || procesandoActividad
                  }
                >
                  {deletingId === actividad._id ? (
                    <i className="fa-solid fa-spinner fa-spin text-white"></i>
                  ) : (
                    <i className="fa-solid fa-trash"></i>
                  )}
                </button>
              )}
            </div>
          ),
        };
      }),
    [
      actividades,
      deleteActividad,
      deletingId,
      generarAsistencia,
      generatingId,
      loading,
      navigate,
      puedeActualizar,
      puedeEliminar,
    ]
  );

  /**
   * DataTable recibe fecha como texto dd/mm/aaaa,
   * por eso no se utiliza Cell para transformarla.
   */
  const columns = useMemo(
    () => [
      {
        Header: "Nombre",
        accessor: "nombreActividad",
      },
      {
        Header: "Descripción",
        accessor: "descripcion",
      },
      {
        Header: "Fecha",
        accessor: "fecha",
      },
      {
        Header: "Acciones",
        accessor: "funciones",
      },
    ],
    []
  );

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* ENCABEZADO */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Lista de Actividades
                </h5>

                <small className="text-secondary">
                  Gestión de actividades, asistencia,
                  generación de documentos, edición y
                  eliminación.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate(-1)}
                  title="Volver"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${
                      loading
                        ? "fa-spinner fa-spin"
                        : "fa-rotate"
                    } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() =>
                      navigate(
                        `/actividad/nuevo/${id}`
                      )
                    }
                    disabled={loading}
                    title="Registrar actividad"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* CONTENIDO */}
          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">
                  Cargando actividades…
                </div>
              </div>
            ) : actividadesConFunciones.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable
                  columns={columns}
                  data={actividadesConFunciones}
                />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">
                  🗂️
                </div>

                <div className="fw-semibold">
                  No hay actividades registradas
                </div>

                <div className="small mb-3">
                  No se encontraron actividades para
                  esta matrícula.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() =>
                      navigate(
                        `/actividad/nuevo/${id}`
                      )
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Crear actividad
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarActividad;